import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, ChevronRight, Eye, Loader2, Mail, Megaphone, Send, User } from 'lucide-react';
import { klarysUrl } from './klarysApi';
import { KlarysCampagne, STATUT_CAMPAGNE, paris } from './KlarysCampagne';

const PERIODES = [['7', '7 j'], ['30', '30 j'], ['90', '90 j'], ['tout', 'Tout']];
const Kpi = ({ icon: Icon, label, valeur, accent, testId }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid={testId}>
    <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500"><Icon className={`h-3.5 w-3.5 ${accent}`} /> {label}</p>
    <p className="mt-1 text-2xl font-bold text-slate-900" data-testid={`${testId}-valeur`}>{valeur}</p>
  </div>
);

const LigneCampagne = ({ c, onOuvrir, testId, individuel }) => {
  const [label, cls] = individuel ? ['Hors campagne', 'bg-slate-100 text-slate-600'] : (STATUT_CAMPAGNE[c.statut] || STATUT_CAMPAGNE.executee);
  return (
    <button type="button" onClick={onOuvrir} data-testid={testId}
      className="flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 text-left text-sm transition-colors hover:border-[#0a3d62]/40 hover:bg-sky-50/40">
      {individuel ? <User className="h-4 w-4 shrink-0 text-slate-400" /> : <Megaphone className="h-4 w-4 shrink-0 text-[#0a3d62]" />}
      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold text-slate-900">{individuel ? 'Envois individuels' : c.titre}</p>
        <p className="truncate text-[11px] text-slate-500">
          {individuel ? `Dernier envoi : ${paris(c.derniere)}` : `${c.statut === 'programmee' ? 'Prévue le' : 'Le'} ${paris(c.date)}${c.critere ? ` · ${c.critere}` : ''}${c.par ? ` · ${c.par}` : ''}`}
        </p>
      </div>
      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${cls}`}>{label}</span>
      <div className="w-36 text-right text-[11px] text-slate-600" data-testid={`${testId}-stats`}>
        <span className="font-semibold text-slate-900">{c.envoyes}</span>{individuel ? '' : ` / ${c.destinataires}`} envoyé(s)
        <span className="block">{c.taux_ouverture} % ouverts{c.echecs ? <b className="ml-1 text-rose-600">· {c.echecs} échec(s)</b> : ''}</span>
      </div>
      <ChevronRight className="h-4 w-4 text-slate-400" />
    </button>
  );
};

export const KlarysEnvois = ({ headers }) => {
  const [periode, setPeriode] = useState('30');
  const [data, setData] = useState(null);
  const [ouverte, setOuverte] = useState(null);
  const charger = useCallback(() => {
    setData(null);
    axios.get(klarysUrl('/envois/tableau'), { headers, params: { periode } })
      .then((r) => setData(r.data)).catch((e) => { toast.error(e.response?.data?.detail || 'Tableau indisponible'); setData({ campagnes: [], indicateurs: {}, individuels: {} }); });
  }, [periode]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { charger(); }, [charger]);
  if (ouverte) return <KlarysCampagne cid={ouverte} periode={periode} headers={headers} onRetour={() => { setOuverte(null); charger(); }} />;
  const ind = data?.indicateurs || {};
  return (
    <div className="h-full overflow-y-auto bg-[#f6f8fb] p-4" data-testid="klarys-envois">
      <div className="mb-3 flex items-center gap-2">
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">Envois Klarys</h3>
        <div className="ml-auto flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="klarys-envois-periode">
          {PERIODES.map(([v, l]) => (
            <button key={v} type="button" onClick={() => setPeriode(v)} data-testid={`klarys-envois-periode-${v}`}
              className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${periode === v ? 'bg-[#0a3d62] text-white' : 'text-slate-600 hover:bg-slate-100'}`}>{l}</button>
          ))}
        </div>
      </div>
      {!data ? <div className="py-10 text-center"><Loader2 className="inline h-5 w-5 animate-spin text-slate-400" /></div> : (
        <>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            <Kpi icon={Send} label="Envoyés" valeur={ind.envoyes ?? 0} accent="text-[#0a3d62]" testId="klarys-kpi-envoyes" />
            <Kpi icon={Eye} label="Ouverts" valeur={ind.ouverts ?? 0} accent="text-emerald-600" testId="klarys-kpi-ouverts" />
            <Kpi icon={Mail} label="Taux d'ouverture" valeur={`${ind.taux_ouverture ?? 0} %`} accent="text-sky-600" testId="klarys-kpi-taux" />
            <Kpi icon={AlertTriangle} label="Échecs" valeur={ind.echecs ?? 0} accent="text-rose-600" testId="klarys-kpi-echecs" />
          </div>
          <p className="mt-1.5 text-[10px] text-slate-400">Taux d'ouverture indicatif (images bloquées ou préchargées par certaines messageries).</p>
          <h4 className="mb-2 mt-4 text-xs font-bold uppercase tracking-wider text-slate-500">Campagnes ({data.campagnes.length})</h4>
          <div className="space-y-2">
            {data.campagnes.map((c, i) => <LigneCampagne key={c.id} c={c} onOuvrir={() => setOuverte(c.id)} testId={`klarys-campagne-${i}`} />)}
            {data.individuels?.total > 0 && <LigneCampagne c={data.individuels} individuel onOuvrir={() => setOuverte('individuels')} testId="klarys-campagne-individuels" />}
            {!data.campagnes.length && !data.individuels?.total && <p className="text-sm text-slate-500" data-testid="klarys-envois-vide">Aucun envoi sur cette période.</p>}
          </div>
        </>
      )}
    </div>
  );
};
